import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { useResource } from "../../hooks/useResource.js";
import { FormShell } from "../../components/forms/FormShell.jsx";
import { FormSection } from "../../components/forms/FormSection.jsx";
import { Input } from "../../components/ui/Input.jsx";
import { Textarea } from "../../components/ui/Textarea.jsx";
import { Button } from "../../components/ui/Button.jsx";
import { PageLoader } from "../../components/ui/Spinner.jsx";
import { ImageUpload } from "../../components/upload/ImageUpload.jsx";
import { PdfUpload } from "../../components/upload/PdfUpload.jsx";

const CURRENT_YEAR = new Date().getFullYear();

const DEFAULTS = {
  year: CURRENT_YEAR,
  title: "",
  awardingBody: "",
  image: "",
  certificatePdf: "",
  description: "",
  sortOrder: 0,
};

function buildPayload(values) {
  const out = { ...values };
  if (out.year !== "") out.year = Number(out.year);
  if (out.sortOrder !== "") out.sortOrder = Number(out.sortOrder);
  return out;
}

export default function AwardFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = id === "new" || !id;

  const { detail, create, update } = useResource("awards", { toastLabel: "award" });
  const { data: existing, isLoading } = detail(isNew ? null : id);

  const form = useForm({ defaultValues: DEFAULTS });
  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = form;

  useEffect(() => {
    if (existing) reset({ ...DEFAULTS, ...existing });
  }, [existing, reset]);

  useEffect(() => {
    document.title = isNew ? "New Award · Admin" : "Edit Award · Admin";
  }, [isNew]);

  const onSubmit = handleSubmit(async (values) => {
    const payload = buildPayload(values);
    if (isNew) {
      await new Promise((resolve, reject) => {
        create.mutate(payload, {
          onSuccess: (created) => {
            navigate(`/awards/${created._id}`);
            resolve();
          },
          onError: reject,
        });
      });
    } else {
      await new Promise((resolve, reject) => {
        update.mutate({ id, data: payload }, { onSuccess: resolve, onError: reject });
      });
    }
  });

  if (!isNew && isLoading) return <PageLoader />;

  return (
    <FormShell
      title={isNew ? "New Award" : `Edit "${existing?.title ?? ""}"`}
      backTo="/awards"
      backLabel="Back to awards"
      onSubmit={onSubmit}
      primaryAction={
        <Button type="submit" loading={isSubmitting}>
          {isNew ? "Create award" : "Save changes"}
        </Button>
      }
      secondaryAction={
        <Button type="button" variant="secondary" onClick={() => navigate("/awards")}>
          Cancel
        </Button>
      }
      sidebar={
        <>
          <FormSection title="Year & sort">
            <Input
              type="number"
              label="Year"
              required
              error={errors.year?.message}
              {...register("year", {
                required: "Year is required",
                min: { value: 1900, message: "Year out of range" },
                max: { value: 2100, message: "Year out of range" },
              })}
            />
            <Input type="number" label="Sort order within year" {...register("sortOrder")} />
          </FormSection>

          <FormSection title="Certificate">
            <Controller
              control={control}
              name="certificatePdf"
              render={({ field }) => (
                <PdfUpload
                  value={field.value}
                  onChange={field.onChange}
                  subdir="certificates"
                />
              )}
            />
          </FormSection>
        </>
      }
    >
      <FormSection title="Basics">
        <Input
          label="Award title"
          required
          error={errors.title?.message}
          {...register("title", { required: "Title is required" })}
        />
        <Input
          label="Awarding body"
          hint="e.g. Housing Industry Association, Master Builders Victoria"
          {...register("awardingBody")}
        />
        <Textarea
          label="Description"
          rows={4}
          hint="One- or two-line context shown on the public Awards page."
          {...register("description")}
        />
      </FormSection>

      <FormSection title="Image">
        <Controller
          control={control}
          name="image"
          render={({ field }) => (
            <ImageUpload
              value={field.value}
              onChange={field.onChange}
              subdir="awards"
              aspect="video"
              hint="Trophy or certificate photo. Optional — when blank, the public page shows a gold Award icon instead."
            />
          )}
        />
      </FormSection>
    </FormShell>
  );
}
